import dayjs from 'dayjs';
import { DateRange } from './types';

export const getCurrentFinancialYear = () => {
  const now = dayjs();
  const currentMonth = now.month() + 1; // dayjs months are 0-indexed
  return currentMonth >= 4 ? now.year() : now.year() - 1;
};

export const getDateRange = (period: string, subPeriod?: string | number) => {
  const fyStart = getCurrentFinancialYear();
  
  switch (period) {
    case 'today':
      return {
        from: dayjs().startOf('day').unix(),
        to: dayjs().endOf('day').unix()
      };
      
    case 'year':
      return {
        from: dayjs(`${fyStart}-04-01`).startOf('day').unix(),
        to: dayjs(`${fyStart + 1}-03-31`).endOf('day').unix()
      };
      
    case 'month':
      if (typeof subPeriod === 'number') {
        const year = subPeriod >= 4 ? fyStart : fyStart + 1;
        const start = dayjs(`${year}-${subPeriod.toString().padStart(2, '0')}-01`).startOf('month');
        return {
          from: start.unix(),
          to: start.endOf('month').unix()
        };
      }
      break;
      
    case 'quarter':
      const quarterMap: Record<string, { start: number[]; end: number[] }> = {
        'Q1': { start: [fyStart, 4], end: [fyStart, 6] },
        'Q2': { start: [fyStart, 7], end: [fyStart, 9] },
        'Q3': { start: [fyStart, 10], end: [fyStart, 12] },
        'Q4': { start: [fyStart + 1, 1], end: [fyStart + 1, 3] }
      };
      
      if (subPeriod && quarterMap[subPeriod as string]) {
        const q = quarterMap[subPeriod as string];
        return {
          from: dayjs(`${q.start[0]}-${q.start[1].toString().padStart(2, '0')}-01`).startOf('month').unix(),
          to: dayjs(`${q.end[0]}-${q.end[1].toString().padStart(2, '0')}-01`).endOf('month').unix()
        };
      }
      break;
      
    case 'half':
      if (subPeriod === 'H1') {
        return {
          from: dayjs(`${fyStart}-04-01`).startOf('day').unix(),
          to: dayjs(`${fyStart}-09-30`).endOf('day').unix()
        };
      } else if (subPeriod === 'H2') {
        return {
          from: dayjs(`${fyStart}-10-01`).startOf('day').unix(),
          to: dayjs(`${fyStart + 1}-03-31`).endOf('day').unix()
        };
      }
      break;
  }
  
  return { from: null, to: null };
};

export const formatDateRange = (from: number | null, to: number | null, period?: string) => {
  if (!from || !to) return '';
  
  const start = dayjs.unix(from);
  const end = dayjs.unix(to);
  
  if (period === 'today' || start.isSame(end, 'day')) {
    return end.format('Do MMM YYYY');
  }
  
  return `${start.format('Do MMM YYYY')} to ${end.format('Do MMM YYYY')}`;
};

export const toUnix = (d: Date) => Math.floor(d.getTime() / 1000);
export const dayRangeForTs = (tsSec: number) => {
  const d = new Date(tsSec * 1000);
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
  const end   = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
  return { from: toUnix(start), to: toUnix(end) };
};

export const normalizeRangeToSeconds = (r?: DateRange): DateRange => {
  if (!r) return { from: null, to: null, period: undefined };
  const fix = (x: number | null) =>
    typeof x === 'number' ? (x > 1e12 ? Math.floor(x / 1000) : x) : null;
  return { from: fix(r.from), to: fix(r.to), period: r.period };
};